/**
 * Shared domain types for Schreibarena.
 *
 * v1 plays with exactly two authors (decision F2), but every structure is
 * indexed/array-based so N authors is a data-model extension, not a rewrite.
 */

export type AuthorIndex = number; // 0-based seat index; seat 0 = "Autor:in 1"

export const MAX_AUTHORS = 2;

/**
 * Subtle per-author tints (decision B5). Seat order = join order (D2).
 * Bright enough to read as a tint on the dark #202020 stage.
 */
export const AUTHOR_COLORS = ['#D10000', '#3d8bff', '#2ecc71', '#b07cff'];

export interface AuthorSeat {
  index: AuthorIndex;
  name: string;
  color: string;
  connected: boolean;
}

export interface JokerDef {
  id: string;
  name: string;
  desc: string;
  /** Wall-clock duration; null = runs until the moderator ends it. */
  durationMs: number | null;
  /** True for cards added on the fly during the evening (decision C3). */
  adhoc?: boolean;
}

export type JokerTarget = 'global' | AuthorIndex; // decision C2

export interface ActiveJoker {
  instanceId: string;
  defId: string;
  name: string;
  desc: string;
  target: JokerTarget;
  startedAt: number; // epoch ms
  /** Wall-clock deadline (decision C1); null while manually timed or paused. */
  endsAt: number | null;
  /** Remaining ms frozen during a global pause (decision A3). */
  remainingMsAtPause: number | null;
  /** Text index where the constraint began (F-22, anthology marking). */
  startTextPos: number;
  endTextPos: number | null;
}

/**
 * Round phases. `intro` (2026-09-08) is the slide the audience sees before
 * the clocks start: the round's Schreibimpuls in title size — or, in a
 * Bildrunde, the comic itself. The moderator leaves it with "Jetzt starten".
 */
export type RoundPhase = 'idle' | 'intro' | 'running' | 'paused' | 'ended';
export type EndReason = 'time' | 'completed' | 'forced';

/**
 * Round formats (2026-09-08). `story` is the classic writing duel; `comic`
 * is the Bildaufgabe warm-up: the authors alternate captioning the six
 * panels of a comic strip, one panel per turn.
 */
export type RoundKind = 'story' | 'comic';

/** Every Bildaufgabe comic is normalised to six panels in reading order. */
export const COMIC_PANELS = 6;
/** Canonical display grid: two columns, three rows (vertical). */
export const COMIC_DISPLAY_COLS = 2;
export const COMIC_DISPLAY_ROWS = 3;

/**
 * A comic uploaded by the moderator, held for the lifetime of the session
 * (2026-09-08). The bytes never travel over the WebSocket — they are served
 * from `/api/sessions/:pin/comics/:id` — so a state broadcast per keystroke
 * stays small. `cols`/`rows` describe the *source* grid; the views re-tile
 * the six panels into the canonical 2×3 vertical layout, which is how a
 * 3×2 landscape strip is brought into the standard format.
 */
export interface ComicDef {
  id: string;
  title: string;
  /** Source grid — cols × rows must be COMIC_PANELS. */
  cols: number;
  rows: number;
  /** Natural pixel size of the uploaded file (for panel aspect ratio). */
  width: number;
  height: number;
  /** MIME type of the stored bytes. */
  mime: string;
  uploadedAt: number;
}

/** Panel geometry for one of the six panels, in the source image. */
export interface ComicPanelBox {
  index: number;
  col: number;
  row: number;
  /** Fractions of the source image, 0..1. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export function comicPanelBoxes(comic: Pick<ComicDef, 'cols' | 'rows'>): ComicPanelBox[] {
  const out: ComicPanelBox[] = [];
  for (let i = 0; i < COMIC_PANELS; i++) {
    const row = Math.floor(i / comic.cols);
    const col = i % comic.cols;
    out.push({
      index: i,
      col,
      row,
      x: col / comic.cols,
      y: row / comic.rows,
      w: 1 / comic.cols,
      h: 1 / comic.rows,
    });
  }
  return out;
}

/** Default Schreibimpuls of a Bildrunde; the moderator may overwrite it. */
export const DEFAULT_COMIC_PROMPT = 'Beschriftet die sechs Panels dieses Comics — ein Panel pro Zug.';

export interface SessionConfig {
  /** Starting time per author, ms (F-5; default 7:00). */
  startMs: number;
  /** Default deduction for a manual penalty, ms (F-25). */
  penaltyStepMs: number;
  /** Default joker duration when a card has none, ms (~2 min). */
  jokerDefaultMs: number;
  /** Story rounds planned for the evening (decision D4; default 5). */
  roundsPlanned: number;
}

export const DEFAULT_CONFIG: SessionConfig = {
  startMs: 7 * 60_000,
  penaltyStepMs: 15_000,
  jokerDefaultMs: 120_000,
  roundsPlanned: 5,
};

/**
 * Live typography of the projection (2026-08-09): the moderation can switch
 * the beamer's story font and size mid-round. Fonts come from the curated
 * list in fonts.ts; fontScale multiplies the base beam font size.
 */
export interface DisplaySettings {
  fontId: string;
  fontScale: number;
}

/** A stretch of text owned by one author; starts lazily at their first char. */
export interface Segment {
  author: AuthorIndex;
  start: number;
}

export type KeystrokeKind = 'char' | 'turn_pass' | 'blocked';

/**
 * One entry of the keystroke timeline (decision E3 / F-32): every keystroke
 * with wall time + elapsed ms, author, char, caret position and clock state.
 */
export interface Keystroke {
  seq: number;
  t: string; // ISO-8601 wall time (F-35)
  elapsedMs: number; // ms since round start (same monotonic session clock)
  author: AuthorIndex;
  key: string; // physical key as reported, e.g. "a", "Enter", "Backspace"
  char: string | null; // character appended, if any
  pos: number; // caret index after the stroke (caret is locked to end, F-15)
  clocksMs: number[]; // remaining ms per seat at this instant
  kind: KeystrokeKind;
}

export interface GameEvent {
  seq: number;
  t: string;
  elapsedMs: number;
  type:
    | 'round_start'
    | 'turn_pass'
    | 'turn_pass_undone'
    | 'pause'
    | 'resume'
    | 'author_frozen'
    | 'joker_start'
    | 'joker_end'
    | 'penalty'
    | 'clock_adjust'
    | 'completion_confirmed'
    | 'completion_retracted'
    | 'round_end';
  data: Record<string, unknown>;
}

/**
 * A clock change worth annotating in the story margin: a penalty (negative
 * deltaMs) or a time credit (positive), anchored at the text position where
 * it happened — rendered like a comment note next to the story.
 */
export interface ClockMark {
  textPos: number;
  author: AuthorIndex;
  deltaMs: number;
  reason: string | null;
  t: string; // ISO wall time
}

/** Snapshot taken at each turn-pass so the moderator can undo it (A4). */
export interface TurnPassSnapshot {
  previousActive: AuthorIndex;
  newActive: AuthorIndex;
  remainingMs: number[];
  passedAt: number; // epoch ms
  /** Bildrunde: panel count before the pass, so an undo re-opens that panel. */
  panelStartsLen: number;
}

export interface RoundState {
  number: number; // 1-based, counts every round of the evening
  /** Format of this round (2026-09-08); Bildrunden are the warm-up. */
  kind: RoundKind;
  /** Comic played in a Bildrunde; null for story rounds. */
  comicId: string | null;
  /**
   * Text index where each panel's caption begins (Bildrunde only). Always
   * starts with [0]; a turn-pass appends the next panel's start, so
   * `panelStarts.length - 1` is the panel currently being captioned.
   */
  panelStarts: number[];
  prompt: string; // the round's Schreibimpuls (F-34)
  phase: RoundPhase;
  activeAuthor: AuthorIndex;
  remainingMs: number[];
  /** Author hit 0:00 and is out of the rotation (decision A2 / F-9). */
  frozen: boolean[];
  confirmedDone: boolean[];
  text: string;
  segments: Segment[];
  activeJokers: ActiveJoker[];
  /** Jokers that already ended, kept for the export's position markers. */
  endedJokers: ActiveJoker[];
  /** Penalties / time credits with their text anchors (margin annotations). */
  marks: ClockMark[];
  keystrokes: Keystroke[];
  events: GameEvent[];
  startedAt: number | null; // epoch ms
  endedAt: number | null;
  endReason: EndReason | null;
  /** Config as it was when this round started (config may change between rounds). */
  configSnapshot: SessionConfig | null;
  lastSettleAt: number;
  undoable: TurnPassSnapshot | null;
  seq: number; // shared counter for keystrokes + events, keeps them alignable
}

export interface SessionState {
  pin: string;
  createdAt: number;
  config: SessionConfig;
  display: DisplaySettings;
  jokerDeck: JokerDef[];
  /** Comic library of this session (Bildaufgabe, 2026-09-08). */
  comics: ComicDef[];
  authors: AuthorSeat[];
  rounds: RoundState[];
}

/* ---------- Wire-facing projections (no keystroke log, trimmed events) ---- */

export interface PublicRound {
  /**
   * 1-based position in the evening's single sequence. Bildrunden count too
   * (2026-09-09): after three warm-up rounds the first writing round is
   * "Runde 4".
   */
  number: number;
  kind: RoundKind;
  comicId: string | null;
  panelStarts: number[];
  prompt: string;
  phase: RoundPhase;
  activeAuthor: AuthorIndex;
  remainingMs: number[];
  frozen: boolean[];
  confirmedDone: boolean[];
  text: string;
  segments: Segment[];
  activeJokers: ActiveJoker[];
  endedJokers: ActiveJoker[];
  marks: ClockMark[];
  startedAt: number | null;
  endedAt: number | null;
  endReason: EndReason | null;
  recentEvents: GameEvent[];
  keystrokeCount: number;
  undoAvailable: boolean;
}

export interface PastRoundSummary {
  number: number;
  kind: RoundKind;
  prompt: string;
  endReason: EndReason | null;
  chars: number;
  words: number;
}

export interface PublicState {
  pin: string;
  config: SessionConfig;
  display: DisplaySettings;
  jokerDeck: JokerDef[];
  comics: ComicDef[];
  authors: AuthorSeat[];
  maxAuthors: number;
  round: PublicRound;
  /**
   * Rounds the evening is planned to run: the configured writing rounds plus
   * however many Bildrunden have actually been played, so "Runde 4 / 8" is
   * true after three warm-up rounds (2026-09-09).
   */
  plannedTotal: number;
  pastRounds: PastRoundSummary[];
}

export function countWords(text: string): number {
  const m = text.trim().match(/\S+/g);
  return m ? m.length : 0;
}

/** The six panel captions of a Bildrunde, sliced out of the round's text. */
export function panelCaptions(round: Pick<PublicRound, 'text' | 'panelStarts'>): string[] {
  const out: string[] = [];
  for (let i = 0; i < COMIC_PANELS; i++) {
    const start = round.panelStarts[i];
    if (start === undefined) {
      out.push('');
      continue;
    }
    const end = round.panelStarts[i + 1] ?? round.text.length;
    out.push(round.text.slice(start, end));
  }
  return out;
}
